import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from '@stores/snackbar';
import { useProctorContext } from '@cont/Quiz/Proctoring';
import useEnroll from './useEnroll';

interface ProctorCredentials {
  sessionId: string;
  sessionToken: string;
  apiUrl: string;
}

interface ProctorHook {
  start: () => Promise<void>;
  stop: (reason?: string) => Promise<void>;
  status?: string;
  on: (
    event: string,
    handler: (...args: any[]) => void
  ) => (() => void) | undefined;
}

interface ProctorContextValue {
  status: string;
  isReady: boolean;
  proctorHook: ProctorHook;
  credentials: ProctorCredentials | null;
  provisionSession: () => Promise<ProctorCredentials>;
  clearSession: () => void;
}

interface UseProctorSessionFlowParams {
  isProctored: boolean;
  onEnrolled: () => void | boolean | Promise<void | boolean>;
}

const TERMINAL_STATUSES = ['completed', 'terminated', 'error'];

/**
 * Shared camera/mic/screen permission dialog -> provision session -> start
 * SDK -> enroll flow, used by every instructions surface that supports
 * proctoring (TestRevamp, the drawer-based UpdatedInstructions footer, and
 * NTA/CBT). `onEnrolled` is the one thing that legitimately differs between
 * callers - what happens right after a successful enroll (navigate to the
 * taking page vs closing a drawer).
 */
const useProctorSessionFlow = ({
  isProctored,
  onEnrolled
}: UseProctorSessionFlowParams) => {
  const { onEnroll } = useEnroll();
  const { open: showSnackbar } = useSnackbar();
  const proctorContext = useProctorContext() as ProctorContextValue | null;

  const [isEnrolling, setIsEnrolling] = useState(false);
  const [shouldStartSdk, setShouldStartSdk] = useState(false);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  // True when the SDK rejected start() because the candidate shared a single
  // window/tab instead of the entire screen - seeds the reopened dialog
  // directly with the access-denied image instead of the default pop-up.
  const [screenShareDenied, setScreenShareDenied] = useState(false);
  const hasStartedSdkRef = useRef(false);
  const listenersAttachedRef = useRef(false);
  const proctorHookRef = useRef<ProctorHook | null>(null);
  // Unsubscribe functions returned by hook.on() - the hook's returned object
  // has no `off` method, so these are the only way to detach listeners.
  const eventUnsubscribersRef = useRef<Array<(() => void) | undefined> | null>(
    null
  );
  // Set when the permission:change listener re-opens the dialog for a
  // screen-share denial, so the session:error/start().catch() handlers that
  // fire right after (the SDK's own fallout from the same denial) know not
  // to also clear/snackbar on top of it.
  const screenShareRecoveryTriggeredRef = useRef(false);
  // Guards against duplicate enroll calls for the same proctoring session
  const hasEnrolledRef = useRef(false);
  const hasFallenBackRef = useRef(false);

  // Detach SDK listeners on unmount only
  useEffect(() => {
    return () => {
      eventUnsubscribersRef.current?.forEach((unsubscribe) => unsubscribe?.());
      eventUnsubscribersRef.current = null;
    };
  }, []);

  const proceedToQuiz = useCallback(async () => {
    try {
      await onEnroll();
      await onEnrolled();
      return true;
    } catch (err: any) {
      showSnackbar({ message: err.message, type: 'error' });
      return false;
    }
  }, [onEnroll, onEnrolled, showSnackbar]);

  const fallbackToUnproctored = useCallback(async () => {
    if (hasFallenBackRef.current || hasEnrolledRef.current) return;
    hasFallenBackRef.current = true;

    proctorContext?.clearSession?.();
    setShouldStartSdk(false);

    await proceedToQuiz();
    setIsEnrolling(false);
  }, [proctorContext, proceedToQuiz]);

  // Enroll into the test once the SDK confirms the session started.
  const handleSessionStarted = useCallback(async () => {
    if (hasEnrolledRef.current) return;
    hasEnrolledRef.current = true;

    try {
      await onEnroll(proctorContext?.credentials?.sessionId);

      setShouldStartSdk(false);
      setIsEnrolling(false);
      const navigated = await onEnrolled();
      if (navigated === false) {
        throw new Error(
          'Failed to navigate to the test after enrollment. Please try again.'
        );
      }
    } catch (enrollError: any) {
      // Allow retry after a failed enrollment
      hasEnrolledRef.current = false;

      // Rollback: stop the (running) SDK session and clear it
      if (proctorContext?.proctorHook?.stop) {
        await proctorContext.proctorHook.stop('ENROLLMENT_FAILED');
      }
      proctorContext?.clearSession?.();

      setShouldStartSdk(false);
      setIsEnrolling(false);
      showSnackbar({
        message:
          enrollError.message || 'Failed to start test. Please try again.',
        type: 'error'
      });
    }
  }, [proctorContext, onEnroll, onEnrolled, showSnackbar]);

  // Triggered by the SDK's permission:change event (kind 'screen', state
  // 'denied') - fires when the candidate shares a single window/tab instead
  // of the entire screen. Camera+mic are still fine, so re-show the
  // permission dialog at the screen step with the access-denied image.
  const recoverFromScreenShareDenial = useCallback(() => {
    if (screenShareRecoveryTriggeredRef.current) return;
    screenShareRecoveryTriggeredRef.current = true;

    proctorContext?.clearSession?.();

    hasStartedSdkRef.current = false;
    hasEnrolledRef.current = false;
    listenersAttachedRef.current = false;
    proctorHookRef.current = null;

    setShouldStartSdk(false);
    setIsEnrolling(false);
    setScreenShareDenied(true);
    setShowPermissionDialog(true);
  }, [proctorContext]);

  const handleSessionError = useCallback(async () => {
    // A screen-share denial already re-opened the dialog; this
    // session:error is just the SDK's fallout from the same denial.
    if (screenShareRecoveryTriggeredRef.current) return;
    await fallbackToUnproctored();
  }, [fallbackToUnproctored]);

  // Attach listeners and start the SDK once the session is ready
  useEffect(() => {
    if (!shouldStartSdk || hasStartedSdkRef.current) return;
    if (!proctorContext?.isReady) return;

    const hook = proctorContext.proctorHook;
    // The hook's first render snapshot can have on: null (see
    // ProctorSession.jsx) - enrollment is driven by the session:started
    // event, so wait until the event API exists before starting.
    if (typeof hook?.start !== 'function' || typeof hook?.on !== 'function') {
      return;
    }

    hasStartedSdkRef.current = true;

    if (!listenersAttachedRef.current) {
      proctorHookRef.current = hook;

      const unsubscribeStarted = hook.on(
        'session:started',
        handleSessionStarted
      );
      const unsubscribeError = hook.on('session:error', handleSessionError);

      // The SDK fires { kind: 'screen', state: 'denied' } when a single
      // window/tab is shared instead of the entire screen, just before it
      // rejects start(). This is the trigger for re-opening the dialog.
      const unsubscribePermission = hook.on(
        'permission:change',
        ({ kind, state }: { kind: string; state: string }) => {
          if (kind === 'screen' && state === 'denied') {
            recoverFromScreenShareDenial();
          }
        }
      );

      eventUnsubscribersRef.current = [
        unsubscribeStarted,
        unsubscribeError,
        unsubscribePermission
      ];

      listenersAttachedRef.current = true;
    }

    hook.start().catch(() => {
      hasStartedSdkRef.current = false;

      proctorContext?.clearSession?.();

      if (screenShareRecoveryTriggeredRef.current) {
        setShouldStartSdk(false);
        setIsEnrolling(false);
        return;
      }

      // Any other start() failure continues the test unproctored.
      fallbackToUnproctored();
    });
    // proctorHook is a dep because on a retry (new session after a completed
    // one) the fresh hook can arrive AFTER the last status transition - the
    // effect must re-run then, or start() is never called.
    // No cleanup here - the unmount-only effect above detaches listeners.
  }, [
    shouldStartSdk,
    proctorContext?.status,
    proctorContext?.proctorHook,
    proctorContext,
    handleSessionStarted,
    handleSessionError,
    recoverFromScreenShareDenial,
    fallbackToUnproctored
  ]);

  useEffect(() => {
    if (shouldStartSdk && proctorContext?.status === 'error') {
      fallbackToUnproctored();
    }
  }, [shouldStartSdk, proctorContext?.status, fallbackToUnproctored]);

  const handlePermissionsGranted = useCallback(async () => {
    setShowPermissionDialog(false);
    setScreenShareDenied(false);
    screenShareRecoveryTriggeredRef.current = false;
    setIsEnrolling(true);
    hasStartedSdkRef.current = false;
    hasEnrolledRef.current = false;
    hasFallenBackRef.current = false;

    try {
      // A session left over from a previous attempt is already finalized -
      // do NOT call stop() on it (stop() POSTs /complete, which fails for
      // an already-completed session). Clearing local state is enough.
      if (proctorContext && TERMINAL_STATUSES.includes(proctorContext.status)) {
        proctorContext.clearSession();
        listenersAttachedRef.current = false;
        proctorHookRef.current = null;

        // Give the old ProctorSession a moment to unmount
        await new Promise((resolve) => {
          setTimeout(resolve, 500);
        });
      }

      await proctorContext?.provisionSession();

      // The SDK acquires its own camera/mic streams and prompts for screen
      // share itself during start()
      setShouldStartSdk(true);
    } catch (error) {
      // Proctoring session creation failed (e.g. the provisioning API call)
      // - don't block or alarm the candidate over this, silently proceed
      // without proctoring instead (no snackbar - error.message here is
      // whatever raw text the backend returned, not fit for display).
      await fallbackToUnproctored();
    }
  }, [proctorContext, fallbackToUnproctored]);

  const handlePermissionsCancelled = useCallback(() => {
    setShowPermissionDialog(false);
    setScreenShareDenied(false);
    screenShareRecoveryTriggeredRef.current = false;

    // Release any abandoned proctoring session (e.g. one already provisioned
    // before cancel, or left over from a screen-share-denial reopen).
    proctorContext?.clearSession?.();

    hasStartedSdkRef.current = false;
    hasEnrolledRef.current = false;
    hasFallenBackRef.current = false;
    listenersAttachedRef.current = false;
    proctorHookRef.current = null;
    setShouldStartSdk(false);
    setIsEnrolling(false);
  }, [proctorContext]);

  // If proctored, show the permission dialog to acquire streams first (this
  // can't synchronously fail, so it always reports success). Otherwise
  // enroll and proceed directly, reporting whether that succeeded.
  const handleProceedClick = useCallback(async () => {
    if (isProctored) {
      setShowPermissionDialog(true);
      return true;
    }
    return proceedToQuiz();
  }, [isProctored, proceedToQuiz]);

  return {
    isEnrolling,
    showPermissionDialog,
    screenShareDenied,
    handleProceedClick,
    handlePermissionsGranted,
    handlePermissionsCancelled
  };
};

export default useProctorSessionFlow;
